import { Box, Collapse, NavLink as MantineNavLink, NavLinkProps } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';
import { IconChevronRight } from '@tabler/icons-react';
import { Children, forwardRef } from 'react';
import { NavLink as RouterNavLink } from 'react-router';

interface RouterNavLinkProps {
  href: string;
  label?: string;
  children?: React.ReactNode;
  className?: string;
  active?: boolean;
  end?: boolean;
}

const RouterNavLinkComponent = forwardRef<HTMLAnchorElement, RouterNavLinkProps & NavLinkProps>((props, ref) => {
  const {
    href,
    label,
    children,
    className,
    active,
    end,
    defaultOpened,
    opened,
    onChange,
    childrenOffset = 'lg',
    rightSection,
    disableRightSectionRotation,
    ...rest
  } = props;

  const hasChildren = Children.count(children) > 0;
  const [_opened, setOpened] = useUncontrolled({
    value: opened,
    defaultValue: defaultOpened,
    finalValue: false,
    onChange,
  });

  return (
    <>
      <MantineNavLink
        {...rest}
        label={label}
        active={active}
        disableRightSectionRotation={hasChildren || disableRightSectionRotation}
        rightSection={
          hasChildren ? (
            <IconChevronRight
              size={16}
              style={{
                transform: _opened ? 'rotate(90deg)' : undefined,
                transition: 'transform 200ms ease',
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setOpened(!_opened);
              }}
            />
          ) : (
            rightSection
          )
        }
        renderRoot={({ className: mantineClassName, ...rootProps }) => (
          <RouterNavLink
            ref={ref}
            to={href}
            end={end}
            className={({ isActive }) => {
              const baseClasses = mantineClassName || '';
              const activeClasses = isActive && active ? 'mantine-NavLink-root[data-active]' : '';
              return `${baseClasses} ${activeClasses} ${className || ''}`.trim();
            }}
            {...rootProps}
          />
        )}
      />
      {hasChildren && (
        <Collapse in={_opened}>
          <Box ps={childrenOffset}>{children}</Box>
        </Collapse>
      )}
    </>
  );
});

RouterNavLinkComponent.displayName = 'RouterNavLink';

export default RouterNavLinkComponent;
